import React, { useState, useEffect, useRef, useContext } from 'react';
import imageCompression from 'browser-image-compression';
import { useParams, useHistory } from 'react-router-dom';
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  Paper,
  Box,
  Typography,
  makeStyles,
  Button,
  IconButton,
  Grid,
  Divider,
  Chip,
  TextField,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  ListSubheader,
  TextareaAutosize
} from '@material-ui/core';
import {
  TextFields,
  Image,
  Videocam,
  AttachFile,
  Audiotrack,
  ContactPhone,
  Schedule,
  Save,
  CheckCircle,
  Delete,
  Edit,
  ArrowUpward,
  FormatBold,
  FormatItalic,
  Title,
  Code,
  Spellcheck
} from '@material-ui/icons';
import api from '../../services/api';
import { getBackendUrl } from '../../config';
import { toast } from 'react-toastify';
import useWhatsApps from '../../hooks/useWhatsApps';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#fff',
    color: '#1a1a2e',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    '& *': {
      color: '#1a1a2e',
    },
    '& .MuiTypography-root': {
      color: '#1a1a2e !important',
    },
    '& .MuiInputBase-input': {
      color: '#1a1a2e !important',
    },
    '& .MuiSvgIcon-root': {
      color: '#1a1a2e !important',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23) !important',
    },
    '& textarea': {
      color: '#1a1a2e !important',
      backgroundColor: '#fff !important',
    },
    '& .MuiButton-containedPrimary': {
      color: '#fff !important',
    },
    '& .MuiButton-containedPrimary *': {
      color: '#fff !important',
    },
    '& .MuiChip-root': {
      backgroundColor: '#2e7d32 !important',
      color: '#fff !important',
    },
    '& .MuiChip-root *': {
      color: '#fff !important',
    }
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1.5rem',
  },
  backButton: {
    textTransform: 'uppercase',
    fontWeight: 600,
    color: '#1976d2',
    borderColor: '#e0e0e0',
  },
  flowTitle: {
    fontSize: '1.2rem',
    fontWeight: 600,
  },
  statusChip: {
    backgroundColor: '#2e7d32',
    color: '#fff',
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: '#fff',
    },
  },
  mainContainer: {
    display: 'flex',
    gap: theme.spacing(4),
  },
  sidebar: {
    width: 320,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  sidebarSectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    fontSize: '1rem',
  },
  toolGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(1.5),
  },
  toolButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#fff',
    cursor: 'pointer',
    height: 100,
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      borderColor: '#bdbdbd',
    },
  },
  toolIcon: {
    fontSize: 32,
    color: '#2e7d32',
    marginBottom: theme.spacing(1),
  },
  toolLabel: {
    fontWeight: 600,
    fontSize: '0.8rem',
    color: '#333',
  },
  contentArea: {
    flex: 1,
  },
  contentTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    fontSize: '1rem',
  },
  contentDesc: {
    color: '#555',
    marginBottom: theme.spacing(3),
    fontSize: '0.9rem',
  },
  flowStepsBox: {
    border: '1px solid #e0e0e0',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    minHeight: 200,
    backgroundColor: '#fff',
  },
  stepsTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    fontSize: '1rem',
  },
  emptyState: {
    color: '#777',
    fontSize: '0.95rem',
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: '1px dotted #e0e0e0',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  stepIndex: {
    backgroundColor: '#f5f5f5',
    color: '#666',
    borderRadius: 4,
    padding: '2px 8px',
    marginRight: theme.spacing(2),
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  stepType: {
    fontWeight: 700,
    marginRight: theme.spacing(2),
    minWidth: 60,
  },
  stepContent: {
    flex: 1,
    color: '#666',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    marginRight: theme.spacing(2),
    minWidth: 0,
  },
  stepActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  // Editor Styles
  editorBox: {
    border: '1px solid #2196f3',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    marginBottom: theme.spacing(2),
  },
  editorInput: {
    width: '100%',
    border: 'none',
    resize: 'vertical',
    outline: 'none',
    fontSize: '1rem',
    fontFamily: 'inherit',
    minHeight: 250,
  },
  editorToolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  formatButton: {
    minWidth: 36,
    height: 36,
    padding: 0,
    color: '#2196f3',
    borderColor: '#e0e0e0',
  },
  editorActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  cancelButton: {
    color: '#1976d2',
    borderColor: '#e0e0e0',
    fontWeight: 600,
  },
  saveButton: {
    backgroundColor: '#1976d2',
    color: '#fff',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#1565c0',
    },
  },
  uploadIcon: {
    position: 'absolute',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    color: '#ff9800',
    cursor: 'pointer',
  },
  editorContainer: {
    position: 'relative',
    border: '1px solid #2196f3',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  imageUploadBox: {
    border: '1px solid #e0e0e0',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  uploadButton: {
    color: '#1976d2',
    borderColor: '#1976d2',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  helperText: {
    fontSize: '0.8rem',
    color: '#666',
    marginBottom: theme.spacing(2),
  }
}));

const TOOLS = [
  { type: 'text', label: 'Texto', icon: TextFields },
  { type: 'image', label: 'Imagen', icon: Image },
  { type: 'video', label: 'Video', icon: Videocam },
  { type: 'file', label: 'Archivo', icon: AttachFile },
  { type: 'audio', label: 'Audio', icon: Audiotrack },
  { type: 'contact', label: 'Contacto', icon: ContactPhone },
];

const ACTIONS = [
  { type: 'test', label: 'Probar', icon: Schedule },
  { type: 'save', label: 'Guardar', icon: Save },
];

export default function CampaignsAdsProgram() {
  const classes = useStyles();
  const history = useHistory();
  const { flowId } = useParams();
  const { user } = useContext(AuthContext);
  const [flow, setFlow] = useState(null);
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Editor State
  const [activeTool, setActiveTool] = useState(null); // 'text', 'image', etc.
  const [editorContent, setEditorContent] = useState('');
  const [editingStepIndex, setEditingStepIndex] = useState(-1);
  const [mediaFile, setMediaFile] = useState(null);
  // Contact Tool State
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactOrg, setContactOrg] = useState('');

  // Test Flow Modal State
  const { whatsApps } = useWhatsApps();
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Variables Menu State
  const [fields, setFields] = useState([]);
  const [folders, setFolders] = useState([]);
  const [variablesMenuAnchor, setVariablesMenuAnchor] = useState(null);

  // IA Corrector Modal State
  const [iaModalOpen, setIaModalOpen] = useState(false);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaCorrectedText, setIaCorrectedText] = useState('');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data } = await api.get("/settings");
        
        let loadedFields = [];
        const registry = data.find(s => s.key === "customFieldRegistry");
        if (registry && registry.value) {
            try {
                loadedFields = JSON.parse(registry.value);
            } catch (e) {}
        }
        
        const systemFields = [
            { id: 'sys-date', name: 'Fecha', system: true, folderId: 'system_folder' },
            { id: 'sys-name', name: 'Nombre Cliente', system: true, folderId: 'system_folder' }
        ];

        systemFields.forEach(sysField => {
            if (!loadedFields.find(f => f.name === sysField.name && f.system)) {
                loadedFields.unshift(sysField);
            }
        });
        
        setFields(loadedFields);

        const foldersReg = data.find(s => s.key === "customFieldFolders");
        if (foldersReg && foldersReg.value) {
            try {
                setFolders(JSON.parse(foldersReg.value));
            } catch (e) {}
        }
      } catch (e) {
        console.error(e);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (testModalOpen) {
      // Pre-fetch some contacts
      handleSearchContacts('');
    }
  }, [testModalOpen]);

  const handleSearchContacts = async (value) => {
    setLoadingContacts(true);
    try {
      const { data } = await api.get('/contacts', {
        params: { searchParam: value, pageNumber: 1, limit: 20 }
      });
      setContactOptions(data.contacts || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleSendTest = async () => {
    if (!selectedWhatsapp || selectedContacts.length === 0) {
       toast.warn('Seleccione conexión y al menos un contacto');
       return;
    }
    setSendingTest(true);
    try {
      for (const contact of selectedContacts) {
       // Fetch full contact to get extraInfo
       const { data: fullContact } = await api.get(`/contacts/${contact.id}`);

       // 1. Get/Create Ticket
       const { data: ticket } = await api.post('/tickets', {
          contactId: contact.id,
          whatsappId: selectedWhatsapp,
          status: 'open'
       });

       // 2. Send Steps
       for (const step of steps) {
          if (step.type === 'text') {
             let body = step.content;

             // Parse system fields
             body = body.replace(/{\s*Nombre Cliente\s*}/gi, fullContact.name || "");
             
             const days = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
             const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
             const today = new Date();
             const formattedDate = `${days[today.getDay()]} ${today.getDate()} de ${months[today.getMonth()]}`;
             body = body.replace(/{\s*Fecha\s*}/gi, formattedDate);
             
             // Parse custom fields
             if (fullContact.extraInfo && fullContact.extraInfo.length > 0) {
                 fullContact.extraInfo.forEach(info => {
                     const regex = new RegExp(`{\\s*${info.name}\\s*}`, 'gi');
                     body = body.replace(regex, info.value || "");
                 });
             }

             await api.post(`/messages/${ticket.id}`, {
                body: body,
                fromMe: true,
                read: true
             });
          } else if (['image', 'video', 'audio', 'file'].includes(step.type)) {
             try {
                if (step.content.startsWith('blob:')) {
                    const response = await fetch(step.content);
                    if (!response.ok) throw new Error('Network response was not ok');
                    const blob = await response.blob();
                    
                    let fileName = step.fileName;
                    if (!fileName || fileName === 'media') {
                       const extMap = {
                          image: 'jpg',
                          video: 'mp4',
                          audio: 'mp3',
                          file: 'doc' 
                       };
                       // Try to get ext from URL
                       let urlExt = '';
                       try {
                          const urlPath = new URL(step.content).pathname;
                          const parts = urlPath.split('.');
                          if (parts.length > 1) urlExt = parts[parts.length - 1];
                       } catch(e) {}
    
                       const mimeExt = blob.type.split('/')[1];
                       const ext = urlExt || mimeExt || extMap[step.type] || 'bin';
                       fileName = `media.${ext}`;
                    }
    
                    const formData = new FormData();
                    formData.append('medias', blob, fileName);
                    formData.append('fromMe', true);
                    
                    await api.post(`/messages/${ticket.id}`, formData);
                 } else if (step.content.startsWith('http')) {
                    // Optimized: Send URL directly to backend
                    await api.post(`/messages/${ticket.id}`, {
                       mediaUrl: step.content,
                       fromMe: true,
                       read: true
                    });
                 } else {
                    // Fallback for text content or other formats
                    await api.post(`/messages/${ticket.id}`, {
                      body: step.content,
                      fromMe: true,
                      read: true
                   });
                 }
             } catch (error) {
                console.error(`Error sending step ${step.id}:`, error);
                toast.error(`Error al enviar paso de ${step.type}. El archivo puede haber expirado. Por favor edite el paso y suba el archivo nuevamente.`);
             }
          } else if (step.type === 'contact') {
             const contactData = JSON.parse(step.content);
              // Create vCard content
              const vcard = `BEGIN:VCARD\nVERSION:3.0\nFN:${contactData.name}\nTEL;type=CELL;type=VOICE;waid=${contactData.phone}:${contactData.phone}\nORG:${contactData.organization}\nEND:VCARD`;
              
              await api.post(`/messages/${ticket.id}`, {
                 body: vcard,
                 fromMe: true,
                 read: true,
                 isContact: true
              });
           }
          
          if (step.delay > 0) {
             await new Promise(r => setTimeout(r, step.delay * 1000));
          }
       }
      }
       toast.success(`Flujo de prueba enviado a ${selectedContacts.length} contactos`);
       setTestModalOpen(false);
       setSelectedContacts([]);
    } catch (e) {
       console.error(e);
       toast.error('Error al enviar prueba');
    } finally {
       setSendingTest(false);
    }
  };

  useEffect(() => {
    if (flowId) loadFlow();
  }, [flowId]);

  const loadFlow = async () => {
    try {
      const { data } = await api.get(`/campaign-flows/${flowId}`);
      setFlow(data);
      if (data.connections) {
        try {
          const parsed = JSON.parse(data.connections);
          setSteps(Array.isArray(parsed) ? parsed : []);
        } catch (e) {
          setSteps([]);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar flujo');
    }
  };

  const handleBack = () => {
    history.push('/campaigns-ads');
  };

  const handleToolClick = (tool) => {
    if (tool.type === 'save') {
      handleSaveFlow();
    } else if (tool.type === 'test') {
      setTestModalOpen(true);
    } else {
      // Open Editor for the tool
      setActiveTool(tool.type);
      setEditorContent('');
      setMediaFile(null);
      setEditingStepIndex(-1);
      setContactName('');
      setContactPhone('');
      setContactOrg('');
    }
  };

  const handleCancelEditor = () => {
    setActiveTool(null);
    setEditorContent('');
    setMediaFile(null);
    setEditingStepIndex(-1);
    setContactName('');
    setContactPhone('');
    setContactOrg('');
  };

  const handleFileSelect = async (e) => {
    let file = e.target.files[0];
    if (!file) return;

    const originalName = file.name; // Guardar el nombre original con su extensión

    // Aplicar compresión forense (Solo si es imagen)
    if (file.type.startsWith('image/')) {
      const options = {
        maxSizeMB: 0.7,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 0.8
      };
      try {
        file = await imageCompression(file, options);
      } catch (error) {
        console.error("Error al comprimir la imagen:", error);
      }
    }

    setMediaFile(file); // Save file object to get name later
    
    // Auto upload immediately to get public URL
    // This simplifies the logic so we don't deal with blobs later
    if (file) {
      const formData = new FormData();
      formData.append("file", file, originalName); // Forzar el nombre original para que el backend detecte la extensión
      
      try {
        const token = localStorage.getItem("token");
        const headers = {};
        if (token) {
           headers.Authorization = `Bearer ${JSON.parse(token)}`;
        }
        
        const { data } = await api.post("/media-upload", formData, { headers });
        const backendUrl = getBackendUrl().replace(/\/$/, ""); 
        const publicUrl = `${backendUrl}/public/${data.filename}`;
        setEditorContent(publicUrl);
        // Ensure we keep the original name even after upload
        // file.name is the original name from user's computer
      } catch (err) {
        console.error(err);
        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || "Error al subir archivo";
        toast.error(`Error al subir archivo: ${errorMessage}`);
      }
    }
  };

  const handleSaveEditor = () => {
    let finalContent = editorContent;
    let finalFileName = undefined;

    if (activeTool === 'contact') {
      if (!contactName.trim() || !contactPhone.trim()) {
         toast.warn('Nombre y Teléfono son requeridos para el contacto');
         return;
      }
      const contactData = {
        name: contactName,
        phone: contactPhone,
        organization: contactOrg
      };
      finalContent = JSON.stringify(contactData);
      finalFileName = contactName;
    } else {
      if (!editorContent.trim() && !mediaFile) {
        toast.warn('El contenido no puede estar vacío');
        return;
      }
      if (mediaFile) {
        finalFileName = mediaFile.name;
      } else if (editingStepIndex >= 0 && steps[editingStepIndex]) {
        // Preserve existing filename if editing and no new file selected
        finalFileName = steps[editingStepIndex].fileName;
        // If fileName is lost/empty but we have a URL, try to extract it from URL as fallback
        if (!finalFileName && typeof editorContent === 'string') {
             try {
                const urlPath = new URL(editorContent).pathname;
                finalFileName = urlPath.split('/').pop();
             } catch(e) {
                finalFileName = editorContent.split('/').pop();
             }
        }
      } else if (typeof editorContent === 'string' && editorContent.startsWith('http')) {
         // If adding new step via URL (not file upload), try to guess filename
         try {
            const urlPath = new URL(editorContent).pathname;
            finalFileName = urlPath.split('/').pop();
         } catch(e) {
            finalFileName = editorContent.split('/').pop();
         }
      }
    }

    const newStep = {
      id: editingStepIndex >= 0 ? steps[editingStepIndex].id : Date.now().toString(),
      type: activeTool,
      content: finalContent,
      delay: 0,
      // Store file info if needed for later upload logic, though for now we just keep the content (url)
      fileName: finalFileName
    };

    let updatedSteps;
    if (editingStepIndex >= 0) {
      updatedSteps = [...steps];
      updatedSteps[editingStepIndex] = newStep;
    } else {
      updatedSteps = [...steps, newStep];
    }

    setSteps(updatedSteps);
    handleCancelEditor(); // Close editor
    
    // Auto-Guardar silencioso en background
    if (flowId) {
      api.put(`/campaign-flows/${flowId}`, {
        connections: JSON.stringify(updatedSteps),
        folderId: flow.folderId
      }).catch(e => console.error("Error auto-saving flow:", e));
    }
  };

  const handleEditStep = (index) => {
    const step = steps[index];
    setActiveTool(step.type);
    
    if (step.type === 'contact') {
      try {
        const contactData = JSON.parse(step.content);
        setContactName(contactData.name || '');
        setContactPhone(contactData.phone || '');
        setContactOrg(contactData.organization || '');
        setEditorContent(step.content); // Keep raw content too just in case
      } catch (e) {
        console.error("Error parsing contact data", e);
      }
    } else {
      setEditorContent(step.content);
    }
    
    setEditingStepIndex(index);
    // Note: mediaFile state won't be restored from step.content URL, 
    // but editorContent will hold the URL which works for preview
  };

  const handleDeleteStep = (index) => {
    const newSteps = [...steps];
    newSteps.splice(index, 1);
    setSteps(newSteps);
    if (editingStepIndex === index) {
      handleCancelEditor();
    }
  };

  const handleSaveFlow = async () => {
    if (!flowId) return;
    setSaving(true);
    try {
      await api.put(`/campaign-flows/${flowId}`, {
        connections: JSON.stringify(steps),
        folderId: flow.folderId
      });
      toast.success('Cambios guardados');
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const insertFormat = (formatStart, formatEnd = formatStart) => {
    const textarea = document.getElementById('editor-textarea-campaigns');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = editorContent || '';

    const before = text.substring(0, start);
    const selected = text.substring(start, end);
    const after = text.substring(end);

    const newContent = before + formatStart + selected + formatEnd + after;
    setEditorContent(newContent);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formatStart.length, end + formatStart.length);
    }, 0);
  };

  const handleCorrectorIA = async () => {
    if (!editorContent) {
      toast.error('Escribe primero el mensaje base para corregir.');
      return;
    }
    setIaLoading(true);
    setIaModalOpen(true);
    try {
      const response = await api.post('/gemini/spin', {
        text: editorContent,
        mode: 'corrector'
      });
      if (response.data && response.data.spunText) {
        setIaCorrectedText(response.data.spunText);
      } else {
        toast.error('La IA no pudo procesar el texto.');
        setIaModalOpen(false);
      }
    } catch (e) {
      console.error(e);
      toast.error('Error al conectar con la IA.');
      setIaModalOpen(false);
    } finally {
      setIaLoading(false);
    }
  };

  const applyIaCorrection = () => {
    setEditorContent(iaCorrectedText);
    setIaModalOpen(false);
    toast.success('Texto corregido aplicado.');
  };
  const handleOpenVariablesMenu = (e) => setVariablesMenuAnchor(e.currentTarget);
  const handleCloseVariablesMenu = () => setVariablesMenuAnchor(null);
  const handleInsertVariable = (variableName) => {
    insertFormat(`{${variableName}}`, '');
    handleCloseVariablesMenu();
  };

  const renderEditor = () => {
    if (!activeTool) return null;

    if (activeTool === 'image') {
      const hasContent = !!editorContent;
      // Get filename from mediaFile if new upload, or extract from URL if editing
      const fileName = mediaFile ? mediaFile.name : (editorContent ? editorContent.split('/').pop() : '');

      return (
        <Box mb={3}>
           <div className={classes.imageUploadBox}>
              <Typography className={classes.helperText}>
                Formatos aceptados: jpg, jpeg, png, webp
              </Typography>
              
              {hasContent && (
                <Box mb={2} display="flex" flexDirection="column" alignItems="flex-start">
                   <Typography variant="body2" style={{ marginBottom: 8, color: '#666' }}>Imagen actual:</Typography>
                   <img 
                      src={editorContent} 
                      alt="Current" 
                      style={{ 
                         width: 100, 
                         height: 100, 
                         objectFit: 'cover', 
                         borderRadius: 4,
                         border: '1px solid #e0e0e0',
                         marginBottom: 16
                      }} 
                   />
                </Box>
              )}

              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileSelect}
              />
              <Button 
                variant="outlined" 
                className={classes.uploadButton}
                onClick={() => fileInputRef.current.click()}
              >
                {hasContent ? 'REEMPLAZAR IMAGEN' : 'SUBIR IMAGEN'}
              </Button>
              
              {hasContent && (
                <Box mb={2}>
                   <div style={{ 
                      backgroundColor: '#e0e0e0', 
                      padding: '6px 12px', 
                      borderRadius: 16, 
                      display: 'inline-block', 
                      fontSize: '0.85rem', 
                      color: '#333',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                   }}>
                      {fileName}
                   </div>
                </Box>
              )}

              <TextField
                fullWidth
                variant="outlined"
                placeholder="URL de imagen (opcional)"
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                margin="dense"
                label="URL de imagen (opcional)"
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { color: '#000', WebkitTextFillColor: '#000' } }}
              />
           </div>
           <div className={classes.editorActions}>
            <Button 
              variant="outlined" 
              className={classes.cancelButton}
              onClick={handleCancelEditor}
            >
              CANCELAR
            </Button>
            <Button 
              variant="contained" 
              className={classes.saveButton}
              onClick={handleSaveEditor}
            >
              GUARDAR PASO
            </Button>
          </div>
        </Box>
      );
    }

    if (activeTool === 'video') {
      const hasContent = !!editorContent;
      const fileName = mediaFile ? mediaFile.name : (editorContent ? editorContent.split('/').pop() : '');

      return (
        <Box mb={3}>
           <div className={classes.imageUploadBox}>
              <Typography className={classes.helperText}>
                Tamaño máximo permitido: 15MB, formato compatible WhatsApp/Telegram: mp4
              </Typography>
              
              {hasContent && (
                <Box mb={2} display="flex" flexDirection="column" alignItems="flex-start">
                   <Typography variant="body2" style={{ marginBottom: 8, color: '#666' }}>Video actual:</Typography>
                   <video 
                      src={editorContent} 
                      style={{ 
                         width: 150, 
                         height: 100, 
                         objectFit: 'cover', 
                         borderRadius: 4,
                         border: '1px solid #e0e0e0',
                         marginBottom: 16
                      }} 
                      controls
                      muted
                   />
                </Box>
              )}

              <input
                type="file"
                accept="video/mp4"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileSelect}
              />
              <Button 
                variant="outlined" 
                className={classes.uploadButton}
                onClick={() => fileInputRef.current.click()}
              >
                {hasContent ? 'REEMPLAZAR VIDEO' : 'SUBIR VIDEO'}
              </Button>
              
              {hasContent && (
                <Box mb={2}>
                   <div style={{ 
                      backgroundColor: '#e0e0e0', 
                      padding: '6px 12px', 
                      borderRadius: 16, 
                      display: 'inline-block', 
                      fontSize: '0.85rem', 
                      color: '#333',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                   }}>
                      {fileName}
                   </div>
                </Box>
              )}

              <TextField
                fullWidth
                variant="outlined"
                placeholder="URL de video (opcional)"
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                margin="dense"
                label="URL de video (opcional)"
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { color: '#000', WebkitTextFillColor: '#000' } }}
              />
           </div>
           <div className={classes.editorActions}>
            <Button 
              variant="outlined" 
              className={classes.cancelButton}
              onClick={handleCancelEditor}
            >
              CANCELAR
            </Button>
            <Button 
              variant="contained" 
              className={classes.saveButton}
              onClick={handleSaveEditor}
            >
              GUARDAR PASO
            </Button>
          </div>
        </Box>
      );
    }

    if (activeTool === 'file') {
      const hasContent = !!editorContent;
      const fileName = mediaFile ? mediaFile.name : (editorContent ? editorContent.split('/').pop() : '');

      return (
        <Box mb={3}>
           <div className={classes.imageUploadBox}>
              <Typography className={classes.helperText}>
                Tamaño máximo permitido: 15MB, aceptados: pdf, doc, docx, htm, html, json, xml, txt, csv, zip, 7z, xls, xlsx, ppt, pptx
              </Typography>
              
              {hasContent && (
                <Box mb={2} display="flex" flexDirection="column" alignItems="flex-start">
                   <Typography variant="body2" style={{ marginBottom: 8, color: '#666' }}>Archivo actual:</Typography>
                   <Box display="flex" alignItems="center" mb={2} p={1} bgcolor="#f5f5f5" borderRadius={4} width="100%">
                      <AttachFile style={{ marginRight: 8, color: '#666' }} />
                      <Typography variant="body2" style={{ wordBreak: 'break-all' }}>{fileName}</Typography>
                   </Box>
                </Box>
              )}

              <input
                type="file"
                accept=".pdf,.doc,.docx,.htm,.html,.json,.xml,.txt,.csv,.zip,.7z,.xls,.xlsx,.ppt,.pptx"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileSelect}
              />
              <Button 
                variant="outlined" 
                className={classes.uploadButton}
                onClick={() => fileInputRef.current.click()}
              >
                {hasContent ? 'REEMPLAZAR ARCHIVO' : 'SUBIR ARCHIVO'}
              </Button>
              
              {hasContent && (
                <Box mb={2}>
                   <div style={{ 
                      backgroundColor: '#e0e0e0', 
                      padding: '6px 12px', 
                      borderRadius: 16, 
                      display: 'inline-block', 
                      fontSize: '0.85rem', 
                      color: '#333',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                   }}>
                      {fileName}
                   </div>
                </Box>
              )}

              <TextField
                fullWidth
                variant="outlined"
                placeholder="URL de archivo (opcional)"
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                margin="dense"
                label="URL de archivo (opcional)"
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { color: '#000', WebkitTextFillColor: '#000' } }}
              />
           </div>
           <div className={classes.editorActions}>
            <Button 
              variant="outlined" 
              className={classes.cancelButton}
              onClick={handleCancelEditor}
            >
              CANCELAR
            </Button>
            <Button 
              variant="contained" 
              className={classes.saveButton}
              onClick={handleSaveEditor}
            >
              GUARDAR PASO
            </Button>
          </div>
        </Box>
      );
    }

    if (activeTool === 'audio') {
      const hasContent = !!editorContent;
      const fileName = mediaFile ? mediaFile.name : (editorContent ? editorContent.split('/').pop() : '');

      return (
        <Box mb={3}>
           <div className={classes.imageUploadBox}>
              <Typography className={classes.helperText}>
                Tamaño máximo permitido: 15MB, formato compatible: mp3
              </Typography>
              
              {hasContent && (
                <Box mb={2} display="flex" flexDirection="column" alignItems="flex-start">
                   <Typography variant="body2" style={{ marginBottom: 8, color: '#666' }}>Audio actual:</Typography>
                   <audio controls src={editorContent} style={{ marginBottom: 16, width: '100%' }} />
                </Box>
              )}

              <input
                type="file"
                accept="audio/mp3,audio/mpeg"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileSelect}
              />
              <Button 
                variant="outlined" 
                className={classes.uploadButton}
                onClick={() => fileInputRef.current.click()}
              >
                {hasContent ? 'REEMPLAZAR AUDIO' : 'SUBIR AUDIO'}
              </Button>
              
              {hasContent && (
                <Box mb={2}>
                   <div style={{ 
                      backgroundColor: '#e0e0e0', 
                      padding: '6px 12px', 
                      borderRadius: 16, 
                      display: 'inline-block', 
                      fontSize: '0.85rem', 
                      color: '#333',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                   }}>
                      {fileName}
                   </div>
                </Box>
              )}

              <TextField
                fullWidth
                variant="outlined"
                placeholder="URL de audio (opcional)"
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                margin="dense"
                label="URL de audio (opcional)"
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { color: '#000', WebkitTextFillColor: '#000' } }}
              />
           </div>
           <div className={classes.editorActions}>
            <Button 
              variant="outlined" 
              className={classes.cancelButton}
              onClick={handleCancelEditor}
            >
              CANCELAR
            </Button>
            <Button 
              variant="contained" 
              className={classes.saveButton}
              onClick={handleSaveEditor}
            >
              GUARDAR PASO
            </Button>
          </div>
        </Box>
      );
    }

    if (activeTool === 'contact') {
      return (
        <Box mb={3}>
           <div className={classes.imageUploadBox}>
              <Box display="flex" alignItems="center" mb={2} p={2} border="1px solid #eee" borderRadius={4}>
                 <ContactPhone style={{ fontSize: 40, color: '#4caf50', marginRight: 16 }} />
                 <Box>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                       {contactName || 'Nombre del contacto'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                       {contactPhone || '+57'}
                    </Typography>
                 </Box>
              </Box>
              
              <Grid container spacing={2}>
                 <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Teléfono"
                      placeholder="Ej: +573001234567"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      margin="dense"
                      inputProps={{ style: { color: '#000', WebkitTextFillColor: '#000' } }}
                    />
                 </Grid>
                 <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Nombre"
                      placeholder="Nombre del contacto"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      margin="dense"
                      inputProps={{ style: { color: '#000', WebkitTextFillColor: '#000' } }}
                    />
                 </Grid>
                 <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Organización"
                      placeholder="Organización (opcional)"
                      value={contactOrg}
                      onChange={(e) => setContactOrg(e.target.value)}
                      margin="dense"
                      inputProps={{ style: { color: '#000', WebkitTextFillColor: '#000' } }}
                    />
                 </Grid>
              </Grid>
           </div>
           <div className={classes.editorActions}>
            <Button 
              variant="outlined" 
              className={classes.cancelButton}
              onClick={handleCancelEditor}
            >
              CANCELAR
            </Button>
            <Button 
              variant="contained" 
              className={classes.saveButton}
              onClick={handleSaveEditor}
            >
              GUARDAR
            </Button>
          </div>
        </Box>
      );
    }

    return (
      <Box mb={3}>
        <div className={classes.editorContainer}>
          <textarea
            id="editor-textarea-campaigns"
            className={classes.editorInput}
            placeholder={activeTool === 'text' ? "Introducir texto" : "Configuración..."}
            value={editorContent}
            onChange={(e) => setEditorContent(e.target.value)}
            autoFocus
          />
          <ArrowUpward className={classes.uploadIcon} fontSize="small" />
        </div>
        
        {activeTool === 'text' && (
          <div className={classes.editorToolbar}>
            <Button variant="outlined" className={classes.formatButton} onClick={handleOpenVariablesMenu} title="Insertar Variables"><Code fontSize="small" /></Button>
            
            <Menu
              anchorEl={variablesMenuAnchor}
              keepMounted
              open={Boolean(variablesMenuAnchor)}
              onClose={handleCloseVariablesMenu}
              PaperProps={{
                style: {
                  maxHeight: 300,
                  width: '25ch',
                },
              }}
            >
              <ListSubheader>Campos del Sistema</ListSubheader>
              {fields.filter(f => f.system).map(field => (
                <MenuItem key={field.id} onClick={() => handleInsertVariable(field.name)}>
                  {field.name}
                </MenuItem>
              ))}
              
              {folders.filter(f => f.id !== 'system_folder').map(folder => {
                const folderFields = fields.filter(f => f.folderId === folder.id && !f.system);
                if (folderFields.length === 0) return null;
                return [
                  <ListSubheader key={`header-${folder.id}`}>{folder.name}</ListSubheader>,
                  ...folderFields.map(field => (
                    <MenuItem key={field.id} onClick={() => handleInsertVariable(field.name)}>
                      {field.name}
                    </MenuItem>
                  ))
                ];
              })}
              
              {/* Unassigned Fields */}
              {fields.filter(f => !f.system && !f.folderId).length > 0 && (
                <ListSubheader>Sin Carpeta</ListSubheader>
              )}
              {fields.filter(f => !f.system && !f.folderId).map(field => (
                <MenuItem key={field.id} onClick={() => handleInsertVariable(field.name)}>
                  {field.name}
                </MenuItem>
              ))}
            </Menu>
            
            {/* Botón Mágico Asistente IA */}
            <Button 
              variant="outlined" 
              style={{ marginLeft: 'auto', borderColor: '#2196f3', color: '#2196f3', fontWeight: 'bold' }} 
              onClick={handleCorrectorIA}
            >
              <Spellcheck fontSize="small" style={{ marginRight: 8 }} />
              Corregir con IA
            </Button>
          </div>
        )}

        <div className={classes.editorActions}>
          <Button 
            variant="outlined" 
            className={classes.cancelButton}
            onClick={handleCancelEditor}
          >
            CANCELAR
          </Button>
          <Button 
            variant="contained" 
            className={classes.saveButton}
            onClick={handleSaveEditor}
          >
            GUARDAR PASO
          </Button>
        </div>
      </Box>
    );
  };

  const renderStepContent = (step) => {
    if (step.type === 'image') {
      const isBlob = step.content && step.content.startsWith('blob:');
      return (
        <Box display="flex" alignItems="center">
          {step.content && (
            <img 
              src={step.content} 
              alt="Preview" 
              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, marginRight: 8, border: isBlob ? '2px solid red' : 'none' }} 
            />
          )}
          {isBlob && <Typography variant="caption" color="error" style={{ fontWeight: 'bold' }}>Re-subir imagen</Typography>}
        </Box>
      );
    }
    if (step.type === 'video') {
      const isBlob = step.content && step.content.startsWith('blob:');
      return (
        <Box display="flex" alignItems="center">
          {step.content && (
            <video 
              src={step.content} 
              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, marginRight: 8, border: isBlob ? '2px solid red' : 'none' }} 
              muted
            />
          )}
          {isBlob && <Typography variant="caption" color="error" style={{ fontWeight: 'bold' }}>Re-subir video</Typography>}
        </Box>
      );
    }
    if (step.type === 'contact') {
      let contactData = {};
      try {
        contactData = typeof step.content === 'string' ? JSON.parse(step.content) : step.content;
      } catch (e) {
        contactData = { name: 'Error', phone: 'Error' };
      }
      return (
        <div 
          style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '4px 8px', 
            borderRadius: 4, 
            color: '#666', 
            fontSize: '0.85rem',
            display: 'inline-block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {`${contactData.name}: ${contactData.phone}${contactData.organization ? ` · ${contactData.organization}` : ''}`}
        </div>
      );
    }
    if (step.type === 'audio' || step.type === 'file') {
      const isBlob = step.content && step.content.startsWith('blob:');
      return (
        <div 
          style={{ 
            backgroundColor: isBlob ? '#ffebee' : '#f5f5f5', 
            padding: '4px 8px', 
            borderRadius: 4, 
            color: isBlob ? '#d32f2f' : '#666', 
            fontSize: '0.85rem',
            display: 'inline-block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            border: isBlob ? '1px solid red' : 'none'
          }}
        >
          {isBlob ? `(Expirado) ${step.fileName || step.content}` : (step.fileName || step.content)}
        </div>
      );
    }
    return <span className={classes.stepContent}>{step.content || '(Sin contenido)'}</span>;
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerTop}>
          <Typography className={classes.headerTitle}>Programación de flujos</Typography>
          <Button 
            variant="outlined" 
            className={classes.backButton}
            onClick={handleBack}
          >
            VOLVER A PASO 1
          </Button>
        </div>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.flowTitle}>{flow?.name || 'Cargando...'}</Typography>
        </Box>
      </div>

      <div className={classes.mainContainer}>
        {/* Left Sidebar */}
        <div className={classes.sidebar}>
          
          {/* Contenido Section - Holds the Editor */}
          <div>
            <Typography className={classes.sidebarSectionTitle}>Contenido</Typography>
            {renderEditor()}
            {/* If no editor is active, we could show a placeholder or nothing, matching the screenshot where it seems empty until clicked */}
            {!activeTool && (
              <Box 
                border="1px solid #e0e0e0" 
                borderRadius={4} 
                p={2} 
                color="text.secondary" 
                fontSize="0.9rem"
                mb={2}
              >
                Selecciona una herramienta abajo para editar contenido.
              </Box>
            )}
          </div>

          {/* Acciones Section - Holds ALL Tools + Actions */}
          <div>
            <Typography className={classes.sidebarSectionTitle}>Acciones</Typography>
            <div className={classes.toolGrid}>
              {TOOLS.map((tool) => (
                <div 
                  key={tool.type} 
                  className={classes.toolButton}
                  onClick={() => handleToolClick(tool)}
                >
                  <tool.icon className={classes.toolIcon} />
                  <span className={classes.toolLabel}>{tool.label}</span>
                </div>
              ))}
              {ACTIONS.map((action) => (
                <div 
                  key={action.type} 
                  className={classes.toolButton}
                  onClick={() => handleToolClick(action)}
                >
                  <action.icon className={classes.toolIcon} />
                  <span className={classes.toolLabel}>{action.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content - Right Side */}
        <div className={classes.contentArea}>
          <Typography className={classes.contentTitle}>Configuración de programación</Typography>
          <Typography className={classes.contentDesc}>
            Todos los cambios se pueden guardar en cualquier momento. Al confirmar los detalles, ajustaré cada bloque.
          </Typography>

          <div className={classes.flowStepsBox}>
            <Typography className={classes.stepsTitle}>Pasos del flujo</Typography>
            
            {steps.length === 0 ? (
              <Typography className={classes.emptyState}>
                Aún no hay pasos. Agrega contenido desde la izquierda.
              </Typography>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {steps.map((step, index) => (
                  <div key={step.id || index} className={classes.stepItem}>
                    <span className={classes.stepIndex}>#{index + 1}</span>
                    <span className={classes.stepType}>
                      {step.type === 'text' && 'Texto'}
                      {step.type === 'image' && 'Imagen'}
                      {step.type === 'video' && 'Video'}
                      {step.type === 'file' && 'Archivo'}
                      {step.type === 'audio' && 'Audio'}
                      {step.type === 'contact' && 'Contacto'}
                    </span>
                    {/* Render content based on type */}
                    {['image', 'video', 'audio', 'file'].includes(step.type) ? renderStepContent(step) : <span className={classes.stepContent}>{step.content || '(Sin contenido)'}</span>}
                    
                    {/* Use flex-grow to push actions to right */}
                    <div style={{ flex: 1 }} /> 

                    <div className={classes.stepActions}>
                      <IconButton size="small" onClick={() => handleEditStep(index)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteStep(index)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Test Flow Modal */}
      <Dialog open={testModalOpen} onClose={() => setTestModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Probar flujo</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Conecciones</InputLabel>
              <Select
                value={selectedWhatsapp}
                onChange={(e) => setSelectedWhatsapp(e.target.value)}
                label="Conecciones"
              >
                {whatsApps.filter(w => w.status === 'CONNECTED' && (user.profile === 'admin' || w.id === user.whatsappId)).map((whatsapp) => (
                  <MenuItem key={whatsapp.id} value={whatsapp.id}>
                    {whatsapp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar por nombre o número"
              value={contactSearch}
              onChange={(e) => {
                  setContactSearch(e.target.value);
                  handleSearchContacts(e.target.value);
              }}
              InputProps={{
                  endAdornment: (
                    <>
                      {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                    </>
                  ),
              }}
            />

            <Paper variant="outlined" style={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                    {contactOptions.map((contact) => {
                        const isSelected = selectedContacts.some(c => c.id === contact.id);
                        return (
                            <ListItem 
                                key={contact.id} 
                                button 
                                onClick={() => {
                                    if (isSelected) {
                                        setSelectedContacts(prev => prev.filter(c => c.id !== contact.id));
                                    } else {
                                        setSelectedContacts(prev => [...prev, contact]);
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <Checkbox
                                        edge="start"
                                        checked={isSelected}
                                        tabIndex={-1}
                                        disableRipple
                                        color="primary"
                                    />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={contact.name} 
                                    secondary={contact.number} 
                                />
                            </ListItem>
                        );
                    })}
                    {contactOptions.length === 0 && !loadingContacts && (
                        <Box p={2} textAlign="center">
                            <Typography variant="body2" color="textSecondary">
                                No se encontraron contactos
                            </Typography>
                        </Box>
                    )}
                </List>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestModalOpen(false)} style={{ color: '#1976d2' }} disabled={sendingTest}>
            CANCELAR
          </Button>
          <Button 
            onClick={handleSendTest} 
            color="primary" 
            variant="contained"
            disabled={sendingTest || !selectedWhatsapp || selectedContacts.length === 0}
          >
            {sendingTest ? 'ENVIANDO...' : `ENVIAR (${selectedContacts.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* IA Corrector Modal */}
      <Dialog open={iaModalOpen} onClose={() => !iaLoading && setIaModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>✨ Corrector de Estilo IA</DialogTitle>
        <DialogContent dividers>
          {iaLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" p={4}>
              <CircularProgress size={40} style={{ marginBottom: 16 }} />
              <Typography>La IA está analizando y estructurando tu mensaje...</Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Texto Original:</Typography>
                <Paper variant="outlined" style={{ padding: 16, backgroundColor: 'var(--color-paper)', color: 'var(--color-text)', minHeight: 200, whiteSpace: 'pre-wrap' }}>
                  {editorContent}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Sugerencia Corregida (IA):</Typography>
                <Paper variant="outlined" style={{ padding: 16, borderColor: '#2196f3', backgroundColor: 'var(--color-paper)', minHeight: 200 }}>
                  <TextareaAutosize
                    value={iaCorrectedText}
                    onChange={(e) => setIaCorrectedText(e.target.value)}
                    style={{ width: '100%', border: 'none', outline: 'none', resize: 'vertical', backgroundColor: 'transparent', color: 'var(--color-text)', fontFamily: 'inherit', fontSize: '1rem', minHeight: 180 }}
                  />
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIaModalOpen(false)} disabled={iaLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={applyIaCorrection} 
            color="primary" 
            variant="contained"
            disabled={iaLoading || !iaCorrectedText}
            startIcon={<CheckCircle />}
          >
            Aprobar y Reemplazar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
